
import React, { useState } from 'react';
import { Download, Filter, RefreshCw, X, ArrowRight, Loader2, StickyNote, AlertTriangle, CheckCircle, Bot } from 'lucide-react';
import { AppSettings, RawNote, ProjectState, AIResponse } from '../types';

interface NotesImporterProps {
  isOpen: boolean;
  onClose: () => void;
  settings: AppSettings;
  projectState: ProjectState;
  onImport: (aiResponse: AIResponse) => void;
}

const NotesImporter: React.FC<NotesImporterProps> = ({ isOpen, onClose, settings, projectState, onImport }) => {
  const [step, setStep] = useState<'filter' | 'review' | 'processing'>('filter');
  const [notes, setNotes] = useState<RawNote[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Filters
  const [domain, setDomain] = useState('P');
  const [minPriority, setMinPriority] = useState(0);
  const [limit, setLimit] = useState(20);

  if (!isOpen) return null;

  const fetchNotes = async () => {
    if (!settings.useBackend || !settings.notesDbConfig) {
      setError("Remote Backend and Notes DB Config required.");
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${settings.backendUrl}/api/bridge/fetch-notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          dbConfig: settings.notesDbConfig,
          filters: { domain, minPriority, limit }
        })
      });
      const data = await response.json();
      if (data.success) {
        setNotes(data.notes);
        setStep('review');
      } else {
        setError(data.error);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const processNotes = async () => {
    setStep('processing');
    try {
      const response = await fetch(`${settings.backendUrl}/api/ingest-notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          notes: notes,
          projectState: projectState
        })
      });
      const data = await response.json();
      onImport(data); // Pass result back to App
      onClose();
    } catch (err: any) {
      setError(err.message);
      setStep('review');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-slate-900 border border-slate-700 rounded-xl shadow-2xl w-full max-w-3xl flex flex-col max-h-[90vh]">
        
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-slate-800">
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <StickyNote className="w-5 h-5 text-amber-500" />
            Import from Notes Pool (KVM8)
          </h2>
          <button onClick={onClose} className="text-slate-500 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 flex-1 overflow-y-auto">
          
          {error && (
            <div className="mb-4 p-3 bg-red-900/20 border border-red-900/50 rounded flex items-center gap-2 text-red-400 text-sm">
              <AlertTriangle className="w-4 h-4" /> {error}
            </div>
          )}

          {step === 'filter' && (
            <div className="space-y-6">
              <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700">
                <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                    <Filter className="w-4 h-4" /> Query Filters
                </h3>
                
                <div className="grid grid-cols-3 gap-4">
                    <div>
                        <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Domain</label>
                        <select 
                            value={domain} onChange={(e) => setDomain(e.target.value)}
                            className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm text-white focus:border-amber-500 outline-none"
                        >
                            <option value="P">P (Projects)</option>
                            <option value="A">A (Agents/AI)</option>
                            <option value="O">O (Operations)</option>
                            <option value="I">I (Identity)</option>
                            <option value="Y">Y (System)</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Min Priority</label>
                        <select 
                            value={minPriority} onChange={(e) => setMinPriority(Number(e.target.value))}
                            className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm text-white focus:border-amber-500 outline-none"
                        >
                            <option value="0">All</option>
                            <option value="3">3+ (Important)</option>
                            <option value="4">4+ (Urgent)</option>
                            <option value="5">5 (Critical)</option>
                        </select>
                    </div>
                     <div>
                        <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Limit</label>
                        <select 
                            value={limit} onChange={(e) => setLimit(Number(e.target.value))}
                            className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm text-white focus:border-amber-500 outline-none"
                        >
                            <option value="10">10 Notes</option>
                            <option value="20">20 Notes</option>
                            <option value="50">50 Notes</option>
                            <option value="100">100 Notes</option>
                        </select>
                    </div>
                </div>
              </div>

              <div className="text-center">
                 <button 
                    onClick={fetchNotes}
                    disabled={loading}
                    className="bg-amber-600 hover:bg-amber-500 text-white px-6 py-3 rounded-lg font-medium inline-flex items-center gap-2 transition-colors disabled:opacity-50"
                 >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : <RefreshCw className="w-4 h-4"/>}
                    Fetch Notes
                 </button>
              </div>
            </div>
          )}

          {step === 'review' && (
             <div className="space-y-4">
                <div className="flex justify-between items-center">
                    <h3 className="text-sm text-slate-400">Found {notes.length} notes matching criteria.</h3>
                    <button onClick={() => setStep('filter')} className="text-xs text-indigo-400 hover:underline">Adjust Filters</button>
                </div>

                <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
                    {notes.map((note) => (
                        <div key={note.id} className="bg-slate-800 p-3 rounded border border-slate-700 flex gap-3">
                            <div className={`w-1 shrink-0 rounded ${note.priority >= 4 ? 'bg-red-500' : 'bg-slate-500'}`}></div>
                            <div className="flex-1">
                                <div className="flex justify-between items-start">
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs font-mono text-slate-500">{note.fr_id || 'NO-REF'}</span>
                                        {note.domain === 'A' && <Bot className="w-3 h-3 text-emerald-400" />}
                                    </div>
                                    <span className="text-[10px] bg-slate-700 px-1.5 py-0.5 rounded text-slate-300">{note.note_type}</span>
                                </div>
                                <p className="text-sm text-slate-200 mt-1">{note.content}</p>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="bg-indigo-900/20 border border-indigo-900/50 p-4 rounded-lg flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-indigo-400 mt-0.5" />
                    <div>
                        <h4 className="text-sm font-semibold text-indigo-300">Ready to Transform?</h4>
                        <p className="text-xs text-indigo-400/80 mt-1">
                            The "Data Miner" Agent will analyze these notes, group them by context/ID, and convert them into a structured task list with dependencies.
                        </p>
                    </div>
                </div>

                <button 
                    onClick={processNotes}
                    className="w-full bg-indigo-600 hover:bg-indigo-500 text-white py-3 rounded-lg font-medium flex items-center justify-center gap-2"
                >
                    <ArrowRight className="w-4 h-4" /> Start Transformation
                </button>
             </div>
          )}

          {step === 'processing' && (
              <div className="flex flex-col items-center justify-center h-64 text-center space-y-4">
                  <Loader2 className="w-12 h-12 text-indigo-500 animate-spin" />
                  <div>
                      <h3 className="text-lg font-medium text-white">Refining Data...</h3>
                      <p className="text-sm text-slate-400">Gemini is structuring your raw notes into a project plan.</p>
                  </div>
              </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default NotesImporter;
